import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import React from 'react'
import { ListOfTopDestination } from '@s4l/stayforlong/marketing/ListOfTopDestination'
import { Button, Carousel, FeaturedImage, Placeholder, Typography } from '@s4l/ds'
import { IconArrowLeft } from '@s4l/icons/IconArrowLeft'
import { IconArrowRight } from '@s4l/icons/IconArrowRight'
import { useS4LContext } from '@s4l/react-context'
import { Link } from '@s4l/react-facade'

import TopDestinationsConnected from './Connected'

import styles from './index.module.css'

export interface ITopDestinationsProps {
  topDestinationsJSON: ReturnType<ListOfTopDestination['toJSON']>
  onDestinationClick?: (data: { index: number; destinationName: string }) => void
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
function noop() {}

function TopDestinations({
  topDestinationsJSON,
  onDestinationClick = noop,
}: Readonly<ITopDestinationsProps>) {
  const { i18n } = useS4LContext()

  if (!topDestinationsJSON) return null

  const topDestinations = ListOfTopDestination.fromJSON(topDestinationsJSON)

  if (topDestinations.isEmpty()) return null

  return (
    <div className={styles.topDestinations} data-testid="top-destinations">
      <Typography as="h5" variant="title" size="s" ellipsis>
        {i18n.formatMessage({ id: 'global.amazing-destinations-title' })}
      </Typography>
      <Carousel preset="multiple-items">
        <Carousel.Content itemsPerPage={[1.5, 3.5, 5]}>
          {topDestinations.map((topDestination, index) => (
            <Link
              key={topDestination.name}
              href={`/${topDestination.slug}`}
              className={styles.link}
              onClick={() => onDestinationClick({ index, destinationName: topDestination.name })}
            >
              <FeaturedImage
                imageUrl={`https://static.stayforlong.com/web/images/${topDestination.imagePath({ mode: 'portrait' })}`} // prettier-ignore
                title={topDestination.name}
                subtitle={`${topDestination.accommodationCount} ${i18n.formatMessage({ id: 'global.hotels' })}`} // prettier-ignore
                showButton={false}
              />
            </Link>
          ))}
        </Carousel.Content>
        <Carousel.Buttons>
          <Button variant="icon-rounded" size="small" leadingIcon={<IconArrowLeft />} />
          <Button variant="icon-rounded" size="small" leadingIcon={<IconArrowRight />} />
        </Carousel.Buttons>
      </Carousel>
    </div>
  )
}

function TopDestinationsPlaceholder() {
  const { i18n } = useS4LContext()

  return (
    <div className={styles.topDestinations} data-testid="top-destinations">
      <Typography as="h5" variant="title" size="s" ellipsis>
        {i18n.formatMessage({ id: 'global.amazing-destinations-title' })}
      </Typography>
      <Carousel preset="multiple-items">
        <Carousel.Content itemsPerPage={[1.5, 3.5, 5]}>
          {Array.from({ length: 5 }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <FeaturedImage.Placeholder key={index} />
          ))}
        </Carousel.Content>
        <Placeholder.Animation />
      </Carousel>
    </div>
  )
}
TopDestinationsPlaceholder.displayName = 'TopDestinationsPlaceholder'

TopDestinations.Connected = withErrorBoundary(TopDestinationsConnected)
TopDestinations.Placeholder = withErrorBoundary(TopDestinationsPlaceholder)

TopDestinations.displayName = 'TopDestinations'
export default withErrorBoundary(TopDestinations) as typeof TopDestinations
